// カテゴリ変更時に、新カテゴリの preset_hashtag_names を取得して
// hashtag input (.js-hashtag) の Tagify インスタンスに反映する。
//
// 設計判断:
// - 新規投稿フォーム / 編集フォーム / 誘導モーダル内 form の全てで動作させる。
//   ページ内に複数の form が同居するケース (例: コミュニティトップ + 誘導モーダル) でも
//   それぞれ独立して動かせるよう、`.js-category-id` の各 instance に対して form 配下で
//   selector を解決する。
// - Tagify のタグデータに `preset: true` カスタム属性を付与して preset 由来かユーザー手入力かを区別する。
//   カテゴリ変更時は preset フラグ付きタグだけ削除するため、ユーザーが意図的に入れたタグは
//   絶対に消えない (=「手入力優先」)。
// - 初期表示時の preset 名は ERB の form の data-initial-preset-hashtag-names に
//   JSON 文字列で埋め込み、JS は初期化時にその値を Tagify 上で preset フラグ付きとして
//   後付けする。元々 ERB で render された他のタグ (誘導モーダルでの guidance.tag_list 等) は
//   そのまま手入力扱いで残す。
// - 既存 category_template.js (テンプレ本文の取得) と責務を分けるため別ファイルで実装する。
class CategoryPresetHashtag {
  // container を渡すと `.js-category-id` の探索範囲を絞れる。
  // 通常 form ページ等は document 全体で問題ないが、誘導モーダルのように
  // 同じ DOM 内に通常 form と同居する可能性がある画面では modalEl を渡してスコープを限定する。
  constructor(container = document) {
    const categorySelects = container.querySelectorAll('.js-category-id');
    categorySelects.forEach((categorySelect) => {
      const form = categorySelect.closest('form');
      if (!form) return;
      this.#bindForm(form, categorySelect);
    });
  }

  #bindForm(form, categorySelect) {
    // Tagify は元の <input> を <tags> 要素でラップし wrapper にも input の className を
    // コピーするため、`.js-hashtag` セレクタは <tags> にもマッチする (querySelector は先頭の <tags>)。
    // __tagify は元の <input> 要素に attach されるので input タグに限定する。
    // 通常 form と誘導モーダル form が同居するページに備え、form 配下に限定して取得する。
    const getTagify = () => {
      const hashtagInput = form.querySelector('input.js-hashtag');
      return hashtagInput && hashtagInput.__tagify;
    };

    // 初期 preset を data 属性から読み取る。
    let initialPreset = [];
    try {
      initialPreset = JSON.parse(form.dataset.initialPresetHashtagNames || '[]');
    } catch {
      initialPreset = [];
    }

    // 初期表示の合成:
    //   - 既存タグ (ERB レンダー時の tag_list 由来) のうち preset 名と一致するものは preset フラグを付ける
    //   - 既存タグのうち preset 名に含まれないものは手入力扱いとしてそのまま
    //   - preset 名に含まれて既存タグに無いものは preset フラグ付きで新規追加
    // removeAllTags は Tagify v4 では同期処理なので、再投入とまとめて 1 度に行う。
    const tagifyInit = getTagify();
    if (tagifyInit && initialPreset.length > 0) {
      const presetSet = new Set(initialPreset);
      const merged = tagifyInit.value.map((t) => ({
        value: t.value,
        preset: presetSet.has(t.value) || Boolean(t.preset),
      }));
      initialPreset.forEach((name) => {
        if (!merged.some((t) => t.value === name)) {
          merged.push({ value: name, preset: true });
        }
      });
      tagifyInit.removeAllTags();
      tagifyInit.addTags(merged);
    }

    // 最新の change イベントで選択された url_name を保持する。
    // ユーザーがカテゴリを高速切り替えしたとき、過去のリクエストのレスポンスが後から
    // 解決して現在の選択と異なる preset を適用してしまう競合状態を避けるためのガード。
    // 各 form 毎に独立した closure で持つ。
    let latestUrlName = '';

    categorySelect.addEventListener('change', async function () {
      const type = this.dataset.type;
      const urlName = this.value;
      latestUrlName = urlName;

      const tagify = getTagify();
      if (!tagify) return;

      // 旧カテゴリの preset 由来タグだけ削除 (ユーザー手入力タグは残る)。
      // removeTags は Promise を返す非同期処理のため、addTags より先に完了させるため await する。
      const oldPresetValues = tagify.value
        .filter((t) => t.preset)
        .map((t) => t.value);
      if (oldPresetValues.length > 0) {
        await tagify.removeTags(oldPresetValues);
      }

      // 未選択 (空文字) の場合はここで終了 (= 旧 preset 削除のみ)
      if (!type || !urlName) return;

      try {
        // type / urlName は通常 'chat' 等の literal と Coorum の slug 規約 (英数+ハイフン)
        // で安全だが、防御的プログラミングとして encodeURIComponent で必ずエンコードする。
        const response = await fetch(
          `/${encodeURIComponent(type)}_categories/${encodeURIComponent(urlName)}/preset_hashtags.json`,
          { headers: { Accept: 'application/json' } },
        );
        if (!response.ok) return;
        // 自分のレスポンスが返ってきた後に、より新しい change で latestUrlName が
        // 上書きされていれば、現在の選択と矛盾するのでこのレスポンスは反映しない。
        if (latestUrlName !== urlName) return;
        const data = await response.json();
        if (latestUrlName !== urlName) return;
        const nextPreset = Array.isArray(data.preset_hashtag_names)
          ? data.preset_hashtag_names
          : [];

        // 新 preset を preset フラグ付きで追加。
        // 既存タグ (= 旧 preset 削除後の残り = ユーザー手入力) と被るものは追加せず、手入力扱いのまま残す。
        const existing = new Set(tagify.value.map((t) => t.value));
        const fresh = nextPreset.filter((name) => !existing.has(name));
        if (fresh.length > 0) {
          tagify.addTags(fresh.map((name) => ({ value: name, preset: true })));
        }
      } catch (e) {
        console.error('[CategoryPresetHashtag] error:', e);
      }
    });
  }
}

export { CategoryPresetHashtag };
